import { useMemo, useState } from 'react';
import { FaEdit, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PageList from '../../../templates/PageList';
import PageSearch from '../../../molecules/PageSearch';
import NewTooltip from '../../../atoms/NewTooltip';
import appConfig from '../../../../config/app-config';
import { CollectionAgreement } from './collectionAgreementModel';
import { collectionAgreementEditPath, collectionAgreementViewPath, collectionAgreementsPath } from './collectionRoutes';

const TABLE_HEADERS = [
    { id: 'externalReference', title: 'Contrato', canSort: false },
    { id: 'policyName', title: 'Política', canSort: false },
    { id: 'assignors', title: 'Cedentes', canSort: false },
    { id: 'boleto', title: 'Gera boleto', canSort: false },
    { id: 'receivingAccountKind', title: 'Tipo de conta', canSort: false },
    { id: 'collect', title: 'Cobrar', canSort: false },
    { id: 'actions', title: 'Ações', canSort: false },
];

const assignorNames = (agreement: CollectionAgreement) =>
    agreement.parties.filter(party => party.role === 'ASSIGNOR').map(party => party.fullName).join(', ') || '-';

const CollectionAgreementListPage = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [search, setSearch] = useState('');
    const { address: API_URL, key: API_KEY } = appConfig.apis.billing;

    const url = useMemo(() => {
        const params = new URLSearchParams({ page: String(page + 1), size: String(pageSize) });
        if (search) params.set('search', search);
        return `${API_URL}/collection-agreements/page-list?${params.toString()}`;
    }, [API_URL, page, pageSize, search]);
    const fetchHeaders = useMemo(() => [{ key: 'x-api-key', value: API_KEY || '' }], [API_KEY]);

    const changeSearch = (term: string) => {
        setSearch(term.trim());
        setPage(0);
    };

    const renderRows = (rows: CollectionAgreement[]) => {
        if (!rows || rows.length === 0) {
            return (
                <tr>
                    <td colSpan={TABLE_HEADERS.length} className="px-4 py-6 text-center text-sm text-gray-500">Nenhum item cadastrado</td>
                </tr>
            );
        }
        return rows.map(agreement => (
            <tr key={agreement.id} onClick={() => navigate(collectionAgreementViewPath(agreement.id))}
                className={`group cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/40 ${agreement.collect ? '' : 'opacity-60'}`}>
                <td className="px-4 py-2 text-sm font-mono">{agreement.externalReference}</td>
                <td className="px-4 py-2 text-sm">{agreement.policyName || '-'}</td>
                <td className="px-4 py-2 text-sm">{assignorNames(agreement)}</td>
                <td className="px-4 py-2 text-sm">{agreement.payment.instruction === 'BOLETO' ? 'Sim' : 'Não'}</td>
                <td className="px-4 py-2 text-sm">{agreement.receivingAccountKindFormatted}</td>
                <td className="px-4 py-2 text-sm">{agreement.collect ? 'Sim' : 'Não'}</td>
                <td className="px-4 py-2 text-sm whitespace-nowrap">
                    <div className="flex items-center justify-end gap-3">
                        <NewTooltip content="Visualizar" alignment="left">
                            <button type="button" aria-label="Visualizar acordo"
                                onClick={event => { event.stopPropagation(); navigate(collectionAgreementViewPath(agreement.id)); }}
                                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
                                <FaEye size={14} />
                            </button>
                        </NewTooltip>
                        <NewTooltip content="Editar" alignment="left">
                            <button type="button" aria-label="Editar acordo"
                                onClick={event => { event.stopPropagation(); navigate(collectionAgreementEditPath(agreement.id)); }}
                                className="text-blue-600 opacity-0 transition-opacity hover:text-blue-700 group-hover:opacity-100 dark:text-blue-400 dark:hover:text-blue-300">
                                <FaEdit size={14} />
                            </button>
                        </NewTooltip>
                    </div>
                </td>
            </tr>
        ));
    };

    return (
        <PageList
            pageTitle="Acordos de cobrança"
            url={url}
            fetchHeaders={fetchHeaders}
            breadcrumb={[{ label: 'Acordos de cobrança', url: collectionAgreementsPath() }]}
            tableHeaders={TABLE_HEADERS}
            defaultBackendHeader={false}
            tableFieldSort="externalReference"
            tableOrderSort="ASC"
            onTableSort={() => undefined}
            renderRows={renderRows}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => { setPageSize(size); setPage(0); }}
            itemsLabel="acordos"
        >
            {{
                filterSlot: (
                    <div className="grid grid-cols-12 gap-1">
                        <PageSearch value={search} label="Procurar por contrato, nome ou CPF/CNPJ" onSearch={changeSearch}
                            filterCleanVisible filterCleanEnabled={Boolean(search)} onFilterClean={() => changeSearch('')}
                            configureVisible={false} className="col-span-12" />
                    </div>
                ),
                controlSlot: (<></>),
            }}
        </PageList>
    );
};

export default CollectionAgreementListPage;
